import React, { forwardRef, useCallback } from 'react';
import styles from './Checkbox.module.css';

export type CheckboxVariant = 'switch' | 'classic';

export interface CheckboxProps {
  label?: React.ReactNode;
  checked: boolean;
  isLabelLink?: boolean;
  labelStyle?: React.CSSProperties;
  onPress: () => void;
  onLinkPress?: () => void;
  disabled?: boolean;
  className?: string;
  boxClassName?: string;
  labelClassName?: string;
  compact?: boolean;
  ariaLabel?: string;
  style?: React.CSSProperties;
  labelPosition?: 'right' | 'left';
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  tabIndex?: number;
  variant?: CheckboxVariant;
  /** Hide the classic checkbox border (useful in filter lists). */
  borderless?: boolean;
}

const Checkbox = forwardRef<HTMLDivElement, CheckboxProps>(({
  label,
  checked,
  isLabelLink = false,
  labelStyle,
  onPress,
  onLinkPress,
  disabled = false,
  className,
  boxClassName,
  labelClassName,
  compact = false,
  ariaLabel,
  style,
  labelPosition = 'right',
  onKeyDown,
  tabIndex,
  variant = 'switch',
  borderless = false,
}, ref) => {
  const resolvedAriaLabel = ariaLabel ?? (typeof label === 'string' ? label : undefined);
  const isClassicVariant = variant === 'classic';

  const handlePress = useCallback(() => {
    if (!disabled) {
      onPress();
    }
  }, [disabled, onPress]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);

    if (event.defaultPrevented || disabled) {
      return;
    }

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      onPress();
    }
  }, [disabled, onKeyDown, onPress]);

  const labelContent = label == null
    ? null
    : isLabelLink
      ? (
        <button
          type="button"
          className={[styles.labelButton, labelClassName ?? ''].filter(Boolean).join(' ')}
          style={labelStyle}
          onClick={(event) => {
            event.stopPropagation();
            if (!disabled) {
              onLinkPress?.();
            }
          }}
          disabled={disabled}
        >
          {typeof label === 'string' ? label : 'Link'}
        </button>
      )
      : (
        <span
          className={[styles.label, labelClassName ?? ''].filter(Boolean).join(' ')}
          style={labelStyle}
        >
          {label}
        </span>
      );

  return (
    <div
      ref={ref}
      role="checkbox"
      aria-checked={checked}
      aria-label={resolvedAriaLabel}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : (tabIndex ?? 0)}
      className={[
        styles.container,
        isClassicVariant ? styles.classic : styles.switch,
        compact ? styles.compact : '',
        disabled ? styles.disabled : '',
        borderless ? styles.borderless : '',
        className ?? '',
      ].filter(Boolean).join(' ')}
      style={style}
      onClick={handlePress}
      onKeyDown={handleKeyDown}
    >
      {labelPosition === 'left' ? labelContent : null}
      <span
        aria-hidden="true"
        className={[
          styles.box,
          checked ? styles.boxChecked : '',
          boxClassName ?? '',
        ].filter(Boolean).join(' ')}
      >
        {isClassicVariant
          ? (checked ? <span className={styles.checkIcon} /> : null)
          : <span className={styles.checkIcon} />}
      </span>
      {labelPosition === 'right' ? labelContent : null}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;